import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { FolderOpen } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import advisorClientService from "../../services/advisorClientService";
import advisorAccountService from "../../services/advisorAccountService";
import { AdvisorAccount } from "../../services/advisorAccountService";

const AdvisorGroupsResolver: React.FC = () => {
  const { user } = useAuth();
  const userRoles = user?.userRoles?.map((ur: { role: string }) => ur.role) || [];
  const hasAdvisorRole = userRoles.includes("ADVISOR");

  const [resolvedAccountId, setResolvedAccountId] = useState<number | null>(null);
  const [advisorAccounts, setAdvisorAccounts] = useState<AdvisorAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolve = async () => {
      try {
        setLoading(true);
        setError(null);

        if (hasAdvisorRole) {
          const { advisorAccountId } = await advisorClientService.listClients();
          if (advisorAccountId) {
            setResolvedAccountId(advisorAccountId);
          } else {
            setError("No advisor account found for your user.");
          }
        } else {
          const accounts = await advisorAccountService.getAdvisorAccounts();
          if (accounts.length === 0) {
            setError("No advisor accounts found.");
          } else if (accounts.length === 1) {
            setResolvedAccountId(accounts[0].id);
          } else {
            setAdvisorAccounts(accounts);
          }
        }
      } catch (err: any) {
        const message = err?.response?.data?.error || err?.message || "Failed to resolve advisor account";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    resolve();
  }, [hasAdvisorRole]);

  if (resolvedAccountId !== null) {
    return <Navigate to={`/advisor/${resolvedAccountId}/groups`} replace />;
  }

  if (loading) {
    return (
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse bg-gray-200 dark:bg-gray-800 rounded-lg h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-[1400px] mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-white">Select Advisor Account</h1>
        <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
          Choose an advisor account to manage its groups.
        </p>
      </div>

      <div className="space-y-2">
        {advisorAccounts.map((account) => (
          <a
            key={account.id}
            href={`/advisor/${account.id}/groups`}
            onClick={(e) => {
              e.preventDefault();
              setResolvedAccountId(account.id);
            }}
            className="flex items-center gap-4 px-4 py-4 bg-white dark:bg-rp-surface border border-gray-200 dark:border-rp-separator rounded-xl hover:bg-gray-50 dark:hover:bg-rp-surface-raised transition-all cursor-pointer"
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-rp-surface-raised">
              <FolderOpen className="w-5 h-5 text-rp-amber" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white">
                {account.broker} - <span className="font-mono">{account.accountId}</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                ID: {account.id}
              </p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

export default AdvisorGroupsResolver;
